import React, { useCallback, useState } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setShowMiniCart, update } from '../../Actions/Cart.action';
import { translate } from '../../Services/translation';
import SideMenu from '../SideMenu/SideMenu';
import OrderRowProductCard from '../OrderRowProductCard/OrderRowProductCard';
import Icon from '../Icon';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import clsx from 'clsx';
import OrderRowProductCardB2B from '../OrderRowProductCard/OrderRowProductCardB2B';
import { OrderRow } from '../../Types/OrderRow';
import LoadingSpinner from '../LoadingSpinner';
import useIsStoreScreen from '../../Hooks/useIsStoreScreen';

const MiniCart = () => {
  const dispatch = useDispatch();
  const {
    showMiniCart,
    orderRows,
    emptyCartLink,
    grandTotal,
    checkoutUrl,
    productTotal,
    screenCheckoutUrl,
  } = useTypedSelector((state) => state.cart);
  const hasOrderRows = useMemo(() => orderRows?.length > 0, [orderRows]);
  const { current } = useTypedSelector((state) => state.userInfo);

  const [
    showCheckoutLinkLoadingSpinner,
    setShowCheckoutLinkLoadingSpinner,
  ] = useState(false);

  const quantity = useMemo(
    () => orderRows.reduce((prev, current) => prev + current.quantity, 0),
    [orderRows]
  );

  const closeMiniCart = () => dispatch(setShowMiniCart(false));

  const Header = () => (
    <div className="flex w-full flex-1 items-center justify-between">
      <h3 className="normal-case">
        {translate('general.cart')} {quantity > 0 ? `(${quantity})` : ''}
      </h3>
      <button className="p-3" onClick={closeMiniCart}>
        <Icon className="h-5 w-5" name="close" />
      </button>
    </div>
  );

  const onCheckoutLinkClick = () => {
    setShowCheckoutLinkLoadingSpinner(true);
  };

  const isStoreScreen = useIsStoreScreen();

  const Footer = hasOrderRows
    ? () => (
        <div className="flex-1 py-2">
          <div className="mb-4 flex items-center justify-between">
            <h4>{translate('general.total')}</h4>
            <h4>{productTotal}</h4>
          </div>
          <a
            href={!isStoreScreen ? checkoutUrl : screenCheckoutUrl}
            className={clsx(
              'btn btn--buy-button btn--large w-full uppercase',
              showCheckoutLinkLoadingSpinner && 'disabled'
            )}
            onClick={onCheckoutLinkClick}
          >
            {!showCheckoutLinkLoadingSpinner &&
              translate('cart.to-checkout')}
            {showCheckoutLinkLoadingSpinner && <LoadingSpinner />}
          </a>
        </div>
      )
    : undefined;

  const removeRow = (row: OrderRow) => {
    dispatch(update(row.rowSystemId, 0));
  };

  return (
    <SideMenu
      className="z-[60] rounded"
      contentClassName="flex-1 border-b border-gray-100 overflow-y-auto scrollbar-none"
      id="mini-cart"
      open={showMiniCart}
      onClosed={closeMiniCart}
      components={{ Header, Footer }}
      showCloseButton={false}
    >
      <div
        className={clsx(
          'flex max-h-full flex-1 flex-col gap-5 overflow-auto px-4 pt-4',
          !hasOrderRows && 'm-auto'
        )}
      >
        {(current?.isBusinessCustomer || isStoreScreen) && hasOrderRows ? (
          orderRows.map((row) => (
            <OrderRowProductCardB2B
              key={row.rowSystemId + 'mini-cart'}
              row={row}
              onRemoveClick={removeRow}
            />
          ))
        ) : hasOrderRows ? (
          orderRows.map((row) => (
            <OrderRowProductCard
              key={row.rowSystemId + 'mini-cart'}
              row={row}
              onRemoveClick={removeRow}
            />
          ))
        ) : (
          <div className="m-auto flex flex-col items-center text-center">
            <Icon name="emoji-dead" className="h-[72px] w-[72px]" />
            <h4 className="mx-0 my-4">{translate('cart.empty')}</h4>
            {emptyCartLink && (
              <a
                href={emptyCartLink}
                className="btn btn--outline btn--large w-full uppercase"
              >
                {translate('cart.empty-cart-link')}
              </a>
            )}
          </div>
        )}
      </div>
    </SideMenu>
  );
};

export default MiniCart;
